// BHP-1038: reusable Tailwind app-shell. Full-width topbar (brand + actions), a
// left sidebar below it, and a content area with a collapse toggle, page title
// and breadcrumb — matching the existing UI. Token-based, light theme.
// Responsive: the sidebar is fixed on desktop (collapsible via the arrow) and an
// off-canvas drawer below md.
import React, { useState } from 'react';
import { ArrowIcon } from './icons';

interface AppShellProps {
  /** Brand/logo shown at the top-left of the topbar. */
  brand?: React.ReactNode;
  /** Sidebar content (identity block + nav). */
  sidebar: React.ReactNode;
  /** Right-aligned topbar content (user menu, actions…). */
  topbar?: React.ReactNode;
  /** Page heading shown in the content header, next to the collapse arrow. */
  title?: React.ReactNode;
  /** Breadcrumb trail shown under the page heading. */
  breadcrumb?: React.ReactNode;
  children: React.ReactNode;
}

export default function AppShell({
  brand,
  sidebar,
  topbar,
  title,
  breadcrumb,
  children
}: AppShellProps): JSX.Element {
  const [open, setOpen] = useState(false); // mobile drawer
  const [collapsed, setCollapsed] = useState(false); // desktop collapse

  // Decoupled: the toggle drives the mobile drawer below md and the desktop
  // collapse at md+, so the two states never drift (the backdrop only closes
  // the drawer). matchMedia is guarded for non-browser/test environments.
  const toggle = (): void => {
    const isDesktop =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(min-width: 768px)').matches;
    if (isDesktop) {
      setCollapsed((value) => !value);
    } else {
      setOpen((value) => !value);
    }
  };

  return (
    <div className="min-h-screen bg-surface-muted font-sans text-ink">
      <header className="fixed inset-x-0 top-0 z-40 flex h-topbar items-center gap-3 border-b border-black/5 bg-surface px-4">
        <div className="text-lg">{brand ?? 'BizHost'}</div>
        <div className="ml-auto flex items-center gap-3">{topbar}</div>
      </header>

      <aside
        className={`fixed bottom-0 left-0 top-topbar z-30 w-sidebar transform overflow-y-auto border-r border-black/10 bg-surface-muted transition-transform ${
          open ? 'translate-x-0' : '-translate-x-full'
        } ${collapsed ? 'md:-translate-x-full' : 'md:translate-x-0'}`}
      >
        <nav className="px-3 py-4">{sidebar}</nav>
      </aside>

      {open ? (
        <div
          className="fixed inset-0 top-topbar z-20 bg-black/40 md:hidden"
          onClick={() => {
            setOpen(false);
          }}
          aria-hidden="true"
          data-testid="sidebar-backdrop"
        />
      ) : null}

      <div
        className={`pt-topbar transition-all ${collapsed ? 'md:pl-0' : 'md:pl-sidebar'}`}
      >
        <div className="flex items-center gap-2 px-4 pt-4">
          <button
            type="button"
            className="rounded border-0 bg-transparent p-1.5 text-ink-muted hover:bg-black/5"
            onClick={toggle}
            aria-label="Toggle sidebar"
          >
            <ArrowIcon
              direction={collapsed ? 'right' : 'left'}
              className="h-5 w-5"
            />
          </button>
          {title != null ? (
            <h1 className="text-xl font-semibold">{title}</h1>
          ) : null}
        </div>
        {breadcrumb != null ? (
          <div className="px-4 pt-1 text-sm text-ink-muted">{breadcrumb}</div>
        ) : null}
        <main className="p-4">{children}</main>
      </div>

      {/* BHP-1038: cyan page frame on all four edges (matches the legacy
          theme accent, which only rendered top/bottom). */}
      <div
        className="pointer-events-none fixed inset-x-0 top-0 z-50 h-1 bg-brand"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none fixed inset-x-0 bottom-0 z-50 h-1 bg-brand"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none fixed inset-y-0 left-0 z-50 w-1 bg-brand"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none fixed inset-y-0 right-0 z-50 w-1 bg-brand"
        aria-hidden="true"
      />
    </div>
  );
}
